import type { ReactNode } from "react";
import { useMemo } from "react";

import MessageLoader from "app/pages/sales-intelligence/modules/ai-agent/components/MessageLoader";
import { StyledSaveSignalDraftButton } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/containers/CraftedSignalStrategy/styles";
import { useSignalDraftForMessage } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/hooks/useSignalDraftForMessage";
import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters";
import signalsService from "app/pages/sales-intelligence/sub-modules/signals/services/signalsService";
import type { IPlainObject } from "app/pages/sales-intelligence/types/plainObject";

import { StyledActionsWrapper } from "../../components/message-strategy/styles";
import type { FiltersStateData } from "../../types/content";
import { FiltersStateStrategyContainer, type FiltersStateStrategyContainerProps } from "../FiltersStateStrategy";

type Props<T extends IPlainObject = IPlainObject> = Pick<
    FiltersStateStrategyContainerProps<T>,
    "filtersManager" | "settings" | "onFilterValueRemove" | "onFilterUpdate"
> & {
    messageId: string;
    loader?: ReactNode;
};

export const CraftedSignalStrategyContainer = ({
    messageId,
    loader = <MessageLoader variant="small" />,
    settings,
    filtersManager,
    onFilterUpdate,
    onFilterValueRemove,
}: Props<LeadsSearchAllFiltersTypes>) => {
    const { signal, saveStatus, createStatus, onSaveSuccess } = useSignalDraftForMessage(messageId);

    const filtersStateData: FiltersStateData | null = useMemo(() => {
        if (!signal) {
            return null;
        }

        // TODO remove data transformation to signals-by-type in scope of SI-7338
        const signalsDTO = signalsService.createSignalsDTOsGroupedByType([signal]);

        return {
            signalsFilters: signalsDTO,
        };
    }, [signal]);

    if (createStatus === "pending") {
        return loader;
    }

    if (!filtersStateData || !signal) {
        return null;
    }

    return (
        <>
            <FiltersStateStrategyContainer
                settings={settings}
                filtersStateData={filtersStateData}
                filtersManager={filtersManager}
                onFilterUpdate={onFilterUpdate}
                onFilterValueRemove={onFilterValueRemove}
            />

            <StyledActionsWrapper>
                <StyledSaveSignalDraftButton
                    id={messageId}
                    signalId={signal.id}
                    isSuccess={saveStatus === "success"}
                    onSaveSuccess={onSaveSuccess}
                />
            </StyledActionsWrapper>
        </>
    );
};
